/**
 * Phase F12 FE-1 — IA-side cheat-detection summary.
 *
 * Surfaces the deterministic cheat score from F12 BE-1 inside the
 * proctor review panel. Renders the overall verdict (severity + score
 * + computed_at), a `proctor_demotion` callout when the score breached
 * the medium threshold, and a per-signal contribution table so the IA
 * can audit "why did this score 47?" without having to count events
 * manually in the timeline.
 *
 * States:
 *   * Loading — skeleton lines (Iron Rule §16: skeleton, not spinner,
 *     for tabular surfaces).
 *   * 404 (no assessment yet) — calm explainer copy. Not all attempts
 *     have assessments: pre-F12 submissions never ran the scorer; an
 *     in-progress attempt won't have one yet either.
 *   * Error — destructive banner with retry CTA.
 *   * Loaded — verdict header + signals table + recompute CTA.
 *
 * Recompute UX:
 *   * Single button opens a small dialog asking "Why are you
 *     recomputing?" — the BE rejects reasons shorter than 4 chars,
 *     and the audit log treats the field as the reviewer's
 *     justification. We surface this as a modal rather than an
 *     inline textarea so the IA actively decides to recompute
 *     instead of accidentally clicking a one-tap button.
 *   * On success, the cache is updated in-place via setQueryData
 *     inside the hook — no spinner round-trip.
 *
 * Privacy / observability:
 *   * The recompute call is audit-logged BE-side (calling reviewer
 *     id + reason). Nothing here lands client-side.
 *   * `scorer_version` is shown in a footnote so reviewers can spot
 *     when an attempt was scored under an older calibration.
 */

import * as React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import { motion } from "motion/react";
import type { CheatAssessment, CheatSeverity, CheatSignal } from "@eduviaos/shared";
import { useCheatAssessment, useRecomputeCheatAssessment } from "@/hooks/useAdmissions";

// ── Display helpers ──────────────────────────────────────────────────────

const SEVERITY_COPY: Record<
  CheatSeverity,
  { label: string; tone: "neutral" | "info" | "warning" | "danger" }
> = {
  clear: { label: "Clear", tone: "neutral" },
  low: { label: "Low risk", tone: "info" },
  medium: { label: "Medium risk", tone: "warning" },
  high: { label: "High risk", tone: "danger" },
};

const SEVERITY_TONE_CLASSES: Record<"neutral" | "info" | "warning" | "danger", string> = {
  neutral: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  info: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  warning: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  danger: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
};

function severityIcon(severity: CheatSeverity): React.ReactNode {
  switch (severity) {
    case "clear":
      return <ShieldCheck className="h-4 w-4" aria-hidden="true" />;
    case "low":
      return <ShieldQuestion className="h-4 w-4" aria-hidden="true" />;
    case "medium":
      return <ShieldAlert className="h-4 w-4" aria-hidden="true" />;
    case "high":
      return <AlertTriangle className="h-4 w-4" aria-hidden="true" />;
  }
}

function formatComputedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ── Component ───────────────────────────────────────────────────────────

interface Props {
  attemptId: string;
}

export function CheatAssessmentSummary({ attemptId }: Props) {
  const query = useCheatAssessment(attemptId);

  if (query.isLoading) {
    return <SkeletonRows />;
  }

  if (query.error) {
    return (
      <div
        role="alert"
        className="border-destructive/30 bg-destructive/5 text-destructive rounded-md border p-3 text-sm"
      >
        <div className="flex items-center justify-between gap-3">
          <span>We couldn't load the cheat-detection assessment.</span>
          <Button type="button" size="sm" variant="outline" onClick={() => query.refetch()}>
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!query.data) {
    return <NoAssessmentState attemptId={attemptId} />;
  }

  return <AssessmentLoaded attemptId={attemptId} assessment={query.data} />;
}

function SkeletonRows() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading cheat assessment">
      <div className="bg-muted h-7 w-1/3 animate-pulse rounded" />
      <div className="bg-muted h-4 w-2/3 animate-pulse rounded" />
      <div className="bg-muted h-4 w-1/2 animate-pulse rounded" />
    </div>
  );
}

function NoAssessmentState({ attemptId }: { attemptId: string }) {
  // Recompute is still offered here — the BE will materialise a fresh
  // assessment from the current event/artifact state, which is exactly
  // what an IA wants on a stale or pre-F12 attempt.
  return (
    <div className="border-border/60 bg-muted/30 rounded-md border border-dashed p-4">
      <p className="text-muted-foreground text-sm">
        No cheat-detection assessment available for this attempt yet. This is normal for attempts
        submitted before F12 landed, or for attempts that haven't been graded yet.
      </p>
      <div className="mt-3">
        <RecomputeButton attemptId={attemptId} variant="outline" label="Compute now" />
      </div>
    </div>
  );
}

function AssessmentLoaded({
  attemptId,
  assessment,
}: {
  attemptId: string;
  assessment: CheatAssessment;
}) {
  const copy = SEVERITY_COPY[assessment.severity];
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.32, 0.72, 0.32, 1] }}
      className="space-y-3"
    >
      {/* Verdict header */}
      <div
        className={`rounded-md border p-3 ${SEVERITY_TONE_CLASSES[copy.tone]}`}
        role="status"
        aria-live="polite"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {severityIcon(assessment.severity)}
            <span className="text-sm font-semibold">{copy.label}</span>
            <span className="text-xs tabular-nums opacity-90">
              · {assessment.overall_score.toFixed(1)} / 100
            </span>
          </div>
          <RecomputeButton attemptId={attemptId} variant="ghost" label="Recompute" />
        </div>
        <p className="mt-1 text-xs opacity-90">
          Computed {formatComputedAt(assessment.computed_at)}
          {assessment.scorer_version && (
            <span className="opacity-70"> · scorer {assessment.scorer_version}</span>
          )}
        </p>
        {assessment.proctor_demotion && (
          <p className="mt-2 text-xs">
            <strong>Proctor demotion applied.</strong> The F10b outcome path treats this attempt as{" "}
            <em>needs review</em> rather than auto-pass. Recompute the outcome separately if you
            want it re-resolved.
          </p>
        )}
      </div>

      {/* Signals table */}
      {assessment.signals.length === 0 ? (
        <p className="text-muted-foreground text-xs">
          No contributing signals — the score is zero.
        </p>
      ) : (
        <SignalsTable signals={assessment.signals} totalScore={assessment.overall_score} />
      )}
    </motion.div>
  );
}

function SignalsTable({ signals, totalScore }: { signals: CheatSignal[]; totalScore: number }) {
  // Sort by descending contribution so the strongest reasons surface
  // at the top of the table — matches how reviewers naturally read
  // the verdict.
  const sorted = React.useMemo(
    () => [...signals].sort((a, b) => b.contribution - a.contribution),
    [signals],
  );
  return (
    <div className="border-border/60 max-h-72 overflow-y-auto rounded-md border">
      <table className="w-full text-xs">
        <caption className="sr-only">
          Cheat-detection signal breakdown contributing to the overall score
        </caption>
        <thead className="bg-muted/50 text-muted-foreground sticky top-0 backdrop-blur">
          <tr className="text-left">
            <th scope="col" className="px-3 py-2 font-medium">
              Signal
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Count
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Weight
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Capped at
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Contribution
            </th>
          </tr>
        </thead>
        <tbody className="divide-border/60 divide-y">
          {sorted.map((signal) => (
            <tr key={signal.kind} className="hover:bg-muted/40 transition-colors duration-[120ms]">
              <td className="px-3 py-2">
                <span className="text-foreground">{signal.label}</span>
                <Badge
                  variant="outline"
                  className="text-muted-foreground ml-2 font-mono text-[10px] font-normal"
                >
                  {signal.kind}
                </Badge>
              </td>
              <td className="text-foreground px-3 py-2 text-right tabular-nums">
                {signal.occurrences}
              </td>
              <td className="text-muted-foreground px-3 py-2 text-right tabular-nums">
                {signal.weight_per_occurrence.toFixed(1)}
              </td>
              <td className="text-muted-foreground px-3 py-2 text-right tabular-nums">
                {signal.cap.toFixed(0)}
              </td>
              <td className="text-foreground px-3 py-2 text-right font-medium tabular-nums">
                +{signal.contribution.toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-muted/40 sticky bottom-0">
          <tr>
            <td colSpan={4} className="text-muted-foreground px-3 py-2 text-right text-[11px]">
              Capped sum (max 100)
            </td>
            <td className="text-foreground px-3 py-2 text-right text-[11px] font-semibold tabular-nums">
              ={totalScore.toFixed(1)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── Recompute dialog ────────────────────────────────────────────────────

function RecomputeButton({
  attemptId,
  variant = "outline",
  label,
}: {
  attemptId: string;
  variant?: "outline" | "ghost" | "default";
  label: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const recompute = useRecomputeCheatAssessment(attemptId);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = reason.trim();
    if (trimmed.length < 4) {
      setError("Please give at least a 4-character reason.");
      return;
    }
    if (trimmed.length > 500) {
      setError("Reason must be 500 characters or fewer.");
      return;
    }
    try {
      await recompute.mutateAsync({ reason: trimmed });
      setOpen(false);
      setReason("");
      toast.success("Recomputed cheat assessment.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't recompute. Try again or refresh.";
      setError(msg);
    }
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant={variant}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {recompute.isPending ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
        )}
        {label}
      </Button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!recompute.isPending) setOpen(next);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
              Recompute cheat assessment
            </DialogTitle>
            <DialogDescription>
              Reruns the deterministic scorer against the current proctor events and artifacts. The
              reason below is audit-logged alongside your reviewer id.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="grid gap-2" noValidate>
            <Label htmlFor="recompute-reason" className="text-xs">
              Reason (4–500 characters)
            </Label>
            <Textarea
              id="recompute-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. New artifacts arrived after the original score; rescoring."
              rows={3}
              maxLength={500}
              minLength={4}
              required
              aria-describedby="recompute-help"
            />
            <p id="recompute-help" className="text-muted-foreground text-[11px]">
              {reason.trim().length}/500 characters.
            </p>
            {error && (
              <p role="alert" className="text-destructive text-xs">
                {error}
              </p>
            )}
            <DialogFooter className="mt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                disabled={recompute.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={recompute.isPending}>
                {recompute.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                )}
                Recompute
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
